import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { TeilnehmerZustand } from '@mcikm-vote/shared';

import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { useEchtzeit } from '../../echtzeit/useEchtzeit';
import { Knopf } from '../../components/Knopf';
import { Logo } from '../../components/Logo';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useTeilnehmerAbmelden, useTeilnehmerZustand } from '../../teilnehmer/teilnehmerApi';
import { AnmeldungSeite } from './AnmeldungSeite';
import { Verbindungsstatus } from './Verbindungsstatus';
import { AbstimmungAnsicht } from './ansichten/AbstimmungAnsicht';
import { ErgebnisAnsicht } from './ansichten/ErgebnisAnsicht';
import { GeschlossenAnsicht } from './ansichten/GeschlossenAnsicht';
import { StimmeAbgegebenAnsicht } from './ansichten/StimmeAbgegebenAnsicht';
import { VeranstaltungBeendetAnsicht } from './ansichten/VeranstaltungBeendetAnsicht';
import { WarteraumAnsicht } from './ansichten/WarteraumAnsicht';

/**
 * Teilnehmerbereich unter `/` (§19).
 *
 * **Welche Ansicht erscheint, entscheidet allein der Server** (§25.2). Diese Komponente wählt nur
 * aus; sie leitet nichts ab. Andernfalls könnten zwei Geräte bei gleichem Serverzustand
 * Verschiedenes zeigen, und ein veralteter Tab könnte eine Ansicht erzwingen, für die er die Daten
 * gar nicht haben dürfte.
 *
 * Die Gestaltung folgt §19.1: eine Aufgabe je Bildschirm, große Flächen, kein Menü.
 */
export function TeilnehmerBereich(): ReactElement {
  // Der Kanal wird erst nach der Anmeldung aufgebaut (§9.1) und nach dem Ende einer Sitzung sofort
  // wieder abgebaut — sonst versuchte ein abgemeldetes Gerät dauerhaft, sich zu verbinden. Der
  // Wert stammt aus einem Effekt und liegt damit einen Durchlauf hinter der Abfrage; das ist
  // unerheblich, weil bis dahin ohnehin der Takt greift.
  const [angemeldet, setAngemeldet] = useState(false);
  const echtzeit = useEchtzeit('teilnehmer', angemeldet);
  const zustand = useTeilnehmerZustand(true, echtzeit.verbunden);
  const abmelden = useTeilnehmerAbmelden();
  const [abmeldungAngefragt, setAbmeldungAngefragt] = useState(false);

  useEffect(() => {
    setAngemeldet(zustand.data !== undefined && !zustand.isError);
  }, [zustand.data, zustand.isError]);

  // Vor der ersten Antwort: nichts anzeigen, was §9.1 nicht zulässt.
  //
  // Bewusst `isFetched` und **nicht** `isPending`: Eine Abfrage ohne Daten fällt bei jeder
  // Neuabfrage auf `pending` zurück. Da hier ein anderes Wurzelelement steht als unten, hängte
  // React die Anmeldeseite dabei aus und neu ein — mitsamt dem eingetippten Zugangscode.
  // `isFetched` zählt dagegen die eingegangenen Antworten und fällt nie wieder auf `false`.
  if (!zustand.isFetched) {
    return (
      <main className="mx-auto flex w-full max-w-xl flex-1 items-center justify-center p-6">
        <p className="text-text-gedaempft text-sm">Wird geladen …</p>
      </main>
    );
  }

  // Keine gültige Sitzung — das ist der Regelfall vor der Anmeldung, kein Fehler (§9.1).
  if (zustand.data === undefined) {
    return <AnmeldungSeite />;
  }

  const daten: TeilnehmerZustand = zustand.data;

  // §22.2: Die Stimmabgabe setzt eine bestätigte Serververbindung voraus. Bestätigt ist sie, wenn
  // der Echtzeitkanal steht **oder** die letzte Zustandsabfrage durchkam — ein blockierter
  // WebSocket ist nach §38 vorgesehen und darf die Stimmabgabe nicht verhindern.
  const verbindungBestaetigt = echtzeit.verbunden || !zustand.isError;

  return (
    <div className="mx-auto flex w-full max-w-xl flex-1 flex-col gap-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <Logo />
        <Knopf
          art="sekundaer"
          className="text-sm"
          disabled={abmelden.isPending}
          onClick={() => {
            setAbmeldungAngefragt(true);
          }}
        >
          Abmelden
        </Knopf>
      </header>

      <Verbindungsstatus
        abfrageErfolgreich={!zustand.isError}
        echtzeitVerbunden={echtzeit.verbunden}
        laeuft={zustand.isFetching}
      />

      {abmeldungAngefragt && (
        <BestaetigungsDialog
          titel="Wirklich abmelden?"
          beschreibung={
            <>
              Nach der Abmeldung gelten Sie als nicht mehr anwesend und können an laufenden und
              künftigen Abstimmungen nicht mehr teilnehmen. Für eine erneute Teilnahme ist eine neue
              Anmeldung mit Ihrem Zugangscode nötig. Eine bereits abgegebene Stimme bleibt erhalten.
            </>
          }
          bestaetigungstext="Abmelden"
          gefahr
          laeuft={abmelden.isPending}
          onBestaetigen={() => {
            abmelden.mutate();
          }}
          onAbbrechen={() => {
            setAbmeldungAngefragt(false);
          }}
        />
      )}

      {/*
        Die Bezeichnung der Veranstaltung steht über jeder Ansicht, die sie nicht ohnehin als
        eigene Kachel zeigt: Wer während einer Abstimmung oder beim Ergebnis auf das Gerät blickt,
        soll ohne Umweg sehen, worum es geht (§19.1, §19.2).
      */}
      {!zeigtVeranstaltungSelbst(daten) && (
        <p className="text-base font-semibold">{daten.veranstaltung.bezeichnung}</p>
      )}

      <main className="flex-1">
        <Ansicht zustand={daten} verbindungBestaetigt={verbindungBestaetigt} />
      </main>

      {/* Kein `<footer>`: Die einzige `contentinfo`-Landmarke der Seite ist die gemeinsame
          Fußzeile aus `AppLayout` (ÄA-2). Zwei davon wären für Vorlesewerkzeuge mehrdeutig. */}
      <div className="border-rand border-t pt-4">
        <ThemeToggle />
      </div>
    </div>
  );
}

/**
 * Ob die gewählte Ansicht die Veranstaltung bereits selbst benennt.
 *
 * Warteraum und Abschlussmeldung tragen Bezeichnung und Datum in ihrer ersten Kachel; dort wäre
 * eine zweite Nennung darüber nur Wiederholung. Die Bedingungen entsprechen bewusst denen in
 * {@link Ansicht} — auch die Rückfälle auf den Warteraum, wenn die zugehörigen Daten fehlen.
 */
function zeigtVeranstaltungSelbst(zustand: TeilnehmerZustand): boolean {
  switch (zustand.ansicht) {
    case 'abstimmung':
      return zustand.abstimmung === null;
    case 'geschlossen':
      return zustand.beendeteAbstimmung === null;
    case 'ergebnis':
      return zustand.beendeteAbstimmung === null || zustand.ergebnis === null;
    case 'abgestimmt':
      return false;
    case 'beendet':
    case 'warteraum':
    default:
      return true;
  }
}

/**
 * Bildet den Serverzustand auf die Ansichten aus §19.2 ab.
 *
 * Die Zuordnung ist bewusst vollständig und ohne Rückfallzweig mit eigener Logik: kommt eine
 * unbekannte Ansicht an, ist der Warteraum die einzige Anzeige, die nichts preisgibt.
 */
function Ansicht({
  zustand,
  verbindungBestaetigt,
}: {
  zustand: TeilnehmerZustand;
  verbindungBestaetigt: boolean;
}): ReactElement {
  switch (zustand.ansicht) {
    case 'abstimmung':
      return zustand.abstimmung === null ? (
        <WarteraumAnsicht veranstaltung={zustand.veranstaltung} />
      ) : (
        <AbstimmungAnsicht
          abstimmung={zustand.abstimmung}
          verbindungBestaetigt={verbindungBestaetigt}
        />
      );

    case 'abgestimmt':
      return <StimmeAbgegebenAnsicht />;

    case 'geschlossen':
      return zustand.beendeteAbstimmung === null ? (
        <WarteraumAnsicht veranstaltung={zustand.veranstaltung} />
      ) : (
        <GeschlossenAnsicht abstimmung={zustand.beendeteAbstimmung} />
      );

    case 'ergebnis':
      return zustand.beendeteAbstimmung === null || zustand.ergebnis === null ? (
        <WarteraumAnsicht veranstaltung={zustand.veranstaltung} />
      ) : (
        <ErgebnisAnsicht abstimmung={zustand.beendeteAbstimmung} ergebnis={zustand.ergebnis} />
      );

    case 'beendet':
      return <VeranstaltungBeendetAnsicht veranstaltung={zustand.veranstaltung} />;

    case 'warteraum':
    default:
      return <WarteraumAnsicht veranstaltung={zustand.veranstaltung} />;
  }
}
